/**
 * Extracts externalIdV2 from investment, with fallback to externalId for backward compatibility
 */
export function getExternalIdV2(investment: {
  externalIdV2?: string;
  externalId: string;
  externalAccountId?: string;
}): string {
  if (investment.externalIdV2) return investment.externalIdV2;

  // Backward compatibility: construct from externalId + externalAccountId
  if (investment.externalAccountId) {
    return `${investment.externalId}::${investment.externalAccountId}`;
  }

  return investment.externalId;
}

function parseExternalIdV2(externalIdV2: string): {
  externalId: string;
  externalAccountId?: string;
} {
  const [externalId, externalAccountId] = externalIdV2.split('::');
  return {
    externalId,
    externalAccountId: externalAccountId || undefined
  };
}

/**
 * Checks if an externalIdV2 matches an investment
 * Handles backward compatibility with old format (just externalId)
 */
export function matchesExternalIdV2(
  externalIdV2: string,
  investment: { externalIdV2?: string; externalId: string; externalAccountId?: string }
): boolean {
  const investmentExternalIdV2 = getExternalIdV2(investment);

  // Exact match (new format → new format, old format → old format)
  if (externalIdV2 === investmentExternalIdV2) return true;

  const parsed = parseExternalIdV2(externalIdV2);
  const parsedInvestment = parseExternalIdV2(investmentExternalIdV2);

  // Different external IDs never match.
  if (parsed.externalId !== parsedInvestment.externalId) return false;

  // Backward compatibility: if either side is old format (no account id),
  // match only on externalId.
  if (!parsed.externalAccountId || !parsedInvestment.externalAccountId) {
    return true;
  }

  // Fully-qualified IDs: both externalId and accountId must match.
  return parsed.externalAccountId === parsedInvestment.externalAccountId;
}
